/**
 * 日付関連のユーティリティ関数
 */

export const TODO_TIME_ZONE = 'Asia/Tokyo';

export interface TokyoDateParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const TOKYO_OFFSET_MS = 9 * 60 * 60 * 1000;

const dateTimeFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: TODO_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

/** 絶対時刻を日本時間の各要素へ変換する。 */
export const getTokyoDateParts = (value: Date | string): TokyoDateParts => {
  const date = value instanceof Date ? value : new Date(value);
  const parts = dateTimeFormatter.formatToParts(date);
  const read = (type: string): number => Number(parts.find(part => part.type === type)?.value);
  return {
    year: read('year'),
    month: read('month'),
    day: read('day'),
    hour: read('hour'),
    minute: read('minute'),
    second: read('second'),
  };
};

export const formatTokyoDateParts = ({ year, month, day }: Pick<TokyoDateParts, 'year' | 'month' | 'day'>): string =>
  `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

/** 絶対時刻を日本時間の日付文字列（YYYY-MM-DD）へ変換する。 */
export const formatTokyoDate = (value: Date | string): string => formatTokyoDateParts(getTokyoDateParts(value));

/** YYYY-MM-DD を日本時間の日付として検証する。 */
export const parseTokyoDateParts = (value: string): Pick<TokyoDateParts, 'year' | 'month' | 'day'> | undefined => {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return undefined;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const candidate = new Date(Date.UTC(year, month - 1, day));
  if (candidate.getUTCFullYear() !== year || candidate.getUTCMonth() !== month - 1 || candidate.getUTCDate() !== day) return undefined;
  return { year, month, day };
};

/** 日本時間の年月日・時刻から、保存用の絶対時刻を生成する。日本は夏時間がないため UTC+09:00 で固定する。 */
export const createTokyoDateTime = ({
  year,
  month,
  day,
  hour = 0,
  minute = 0,
  second = 0,
}: Partial<TokyoDateParts> & Pick<TokyoDateParts, 'year' | 'month' | 'day'>): Date =>
  new Date(Date.UTC(year, month - 1, day, hour, minute, second) - TOKYO_OFFSET_MS);

/** 日本時間の日付文字列の開始時刻を絶対時刻として返す。 */
export const startOfTokyoDate = (value: string): Date | undefined => {
  const parts = parseTokyoDateParts(value);
  return parts ? createTokyoDateTime(parts) : undefined;
};

/** 日本時間の日付文字列を指定日数だけ進める。 */
export const addTokyoDays = (value: string, days: number): string | undefined => {
  const parts = parseTokyoDateParts(value);
  if (!parts) return undefined;
  const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + days));
  return formatTokyoDateParts({
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
  });
};

/** 現在の日本時間の日付文字列を返す。 */
export const getTodayTokyoDateString = (): string => formatTokyoDate(new Date());

/**
 * 現在の日付をYYYYMMDD形式で取得
 */
export const getTodayDateString = (): string => {
  return getTodayTokyoDateString().replace(/-/g, '');
};

/**
 * Date オブジェクトをYYYYMMDD形式の文字列に変換
 */
export const formatDateToUrlFormat = (date: Date): string => {
  return formatTokyoDate(date).replace(/-/g, '');
};

/**
 * YYYYMMDD形式の文字列をYYYY-MM-DD形式に変換
 */
export const urlFormatToIsoDate = (urlDate: string): string => {
  if (urlDate.length !== 8) return '';
  const year = urlDate.substring(0, 4);
  const month = urlDate.substring(4, 6);
  const day = urlDate.substring(6, 8);
  return `${year}-${month}-${day}`;
};

/**
 * YYYY-MM-DD形式の文字列をYYYYMMDD形式に変換
 */
export const isoDateToUrlFormat = (isoDate: string): string => {
  return isoDate.replace(/-/g, '');
};

/**
 * YYYYMMDD形式の文字列が有効な日付かチェック
 */
export const isValidDateUrl = (urlDate: string): boolean => {
  // 8桁の数字かチェック
  if (!/^\d{8}$/.test(urlDate)) {
    return false;
  }

  const year = parseInt(urlDate.substring(0, 4), 10);
  const month = parseInt(urlDate.substring(4, 6), 10);
  const day = parseInt(urlDate.substring(6, 8), 10);

  // 基本的な範囲チェック
  if (year < 1900 || year > 9999) return false;
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;

  // 実際に日付として有効かチェック
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year &&
         date.getMonth() === month - 1 &&
         date.getDate() === day;
};

/**
 * 2月29日の閏年チェック（YYYY0229の場合のみ）
 */
export const handleLeapYearRedirect = (urlDate: string): string | null => {
  // YYYY0229の場合のみ処理
  if (!urlDate.endsWith('0229')) {
    return null;
  }

  const year = parseInt(urlDate.substring(0, 4), 10);
  
  // 閏年かチェック
  const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
  
  if (!isLeapYear) {
    // 閏年でない場合は3月1日にリダイレクト
    return `${year}0301`;
  }
  
  return null;
};
